import { errorConfig, RequestError, ResponseParseError } from '@/requestErrorConfig';
import { sessionRevision } from './session';

export type RequestOptions = {
  method?: string;
  params?: object;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  timeout?: number;
  /** Suppresses error presentation; API 401 still invalidates the entire session. */
  skipErrorHandler?: boolean;
};

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();
const defaultTimeout = 30_000;
const responseData = new WeakMap<Response, unknown>();

/** Read the JSON already validated by requestFetch without parsing the body again. */
export function getResponseData<T>(response: Response): T {
  return responseData.get(response) as T;
}

export function resolveRequestUrl(url: string) {
  if (/^https?:\/\//i.test(url) || !apiBaseUrl) {
    return new URL(url, window.location.origin);
  }

  const baseUrl = new URL(`${apiBaseUrl.replace(/\/+$/, '')}/`, window.location.origin);
  return new URL(url.replace(/^\/+/, ''), baseUrl);
}

function createUrl(url: string, params?: object) {
  const requestUrl = resolveRequestUrl(url);

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        requestUrl.searchParams.append(key, String(item));
      }
      continue;
    }

    requestUrl.searchParams.set(key, String(value));
  }

  return requestUrl;
}

export async function request<T>(url: string, options?: RequestOptions): Promise<T> {
  const revision = sessionRevision();
  const method = (
    options?.method ?? (options?.data === undefined ? 'GET' : 'POST')
  ).toUpperCase();
  const requestUrl = createUrl(url, options?.params);
  const requestInstance = new Request(requestUrl, {
    method,
    headers: {
      ...(options?.data === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...options?.headers,
    },
    body: options?.data === undefined ? undefined : JSON.stringify(options.data),
    credentials: options?.credentials ?? 'same-origin',
    signal: options?.signal,
  });

  let response = await requestFetch(requestInstance, undefined, options);
  const data = getResponseData<T>(response);
  if (revision !== sessionRevision()) {
    throw new DOMException('Session changed while reading response', 'AbortError');
  }

  for (const interceptor of errorConfig.responseInterceptors) {
    response = await interceptor(response, data);
  }

  return data as T;
}

export async function requestFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: RequestOptions,
) {
  const revision = sessionRevision();
  const sourceRequest = new Request(input, init);
  const interceptedOptions = errorConfig.requestInterceptors.reduce<RequestOptions>(
    (currentOptions, interceptor) => interceptor(currentOptions, sourceRequest.url),
    {
      ...options,
      headers: Object.fromEntries(sourceRequest.headers.entries()),
    },
  );
  // AbortSignal.any/timeout are missing in older mobile browsers. Compose with
  // AbortController so startup requests still work and cancellation is retained.
  const controller = new AbortController();
  let timedOut = false;
  const forwardAbort = () => controller.abort(sourceRequest.signal.reason);
  if (sourceRequest.signal.aborted) forwardAbort();
  else sourceRequest.signal.addEventListener('abort', forwardAbort, { once: true });
  const timeoutId = setTimeout(() => {
    if (controller.signal.aborted) return;
    timedOut = true;
    controller.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, options?.timeout ?? defaultTimeout);
  const requestInstance = new Request(sourceRequest, {
    headers: interceptedOptions.headers,
    signal: controller.signal,
  });

  try {
    const response = await fetch(requestInstance);
    if (revision !== sessionRevision()) {
      throw new DOMException('Session changed while request was pending', 'AbortError');
    }
    if (!response.ok) {
      throw new RequestError(
        `Request failed: ${response.status} ${response.statusText}`,
        { response, request: requestInstance },
      );
    }

    // Successful payloads are consumed once and exposed via getResponseData.
    // Cloning here leaves an unread stream buffering the entire response.
    const body = response.status === 204 ? '' : await response.text();
    if (revision !== sessionRevision()) {
      throw new DOMException('Session changed while reading response', 'AbortError');
    }
    let data: unknown;
    if (body.trim()) {
      try {
        data = JSON.parse(body);
      } catch {
        throw new ResponseParseError();
      }
    }

    errorConfig.errorConfig.errorThrower(data, requestInstance);
    responseData.set(response, data);
    return response;
  } catch (error) {
    const requestError = timedOut
      ? new DOMException('Request timed out', 'TimeoutError')
      : error instanceof TypeError
        ? new RequestError(error.message, { request: requestInstance })
        : error;
    errorConfig.errorConfig.errorHandler(requestError, options);
    throw requestError;
  } finally {
    clearTimeout(timeoutId);
    sourceRequest.signal.removeEventListener('abort', forwardAbort);
  }
}
